/**
 * @file not-found-filter.ts
 * @description Журнал «Не найдено» — что записывать и как считать дни
 * (phase19.16, партия «SEO-техника», v0.19.65; site-builder.md SB148; seo.md
 * SEO2; db-design-seo-tech §3.1, §4.2–§4.3; план фазы §3 п. 11–13; решения
 * оркестратора §3а п. 7, 8, 11, 14).
 *
 * Зачем в shared: фильтр выполняют две стороны — витрина (`siteNotFound()`
 * страниц и маяк перехода `/site-not-found` решают, слать ли запись) и api (`POST /public/site-not-found` повторяет
 * фильтр: запись не должна обходить его прямым запросом). Разойдись они —
 * журнал засоряется ботами или теряет настоящие битые ссылки.
 *
 * Что исключается (SB148): адреса с расширением в последнем сегменте
 * (`.php`, `.env`, `.xml` — такие нельзя переадресовать по SB131), сегменты
 * с ведущей точкой, `wp-…`, `cgi-bin`, `\` и `%5C`, путь длиннее 512 знаков
 * до приведения, невалидный UTF-8, запросы посетителей без `Accept: text/html`
 * (поисковые роботы по User-Agent пишутся без этой проверки — они шлют
 * `*\/*`; уточнение оркестратора к SB148, решение §3а (в)),
 * служебные адреса и корни разделов по таблице маршрутов (`classifySitePath`
 * — пишем только класс `page`), ссылки с ключами из писем (их префиксы —
 * служебные адреса SB131), сотрудник с пропуском и предпросмотр.
 *
 * Что пишем: путь в той же форме, что `SiteRedirect.fromPath` и
 * `SiteNotFoundHidden.path` (`normalizeSiteRedirectPath(raw).path`, без
 * строки запроса — ПДн в query не храним, SEO2), и класс посетителя по
 * User-Agent (сам заголовок не хранится — его можно подделать, подсказка в
 * кабинете).
 */

import { classifySitePath, normalizeSiteRedirectPath, SITE_REDIRECT_PATH_MAX } from "../site-redirect/paths.js";

// ── Пределы журнала (SB148, db-design-seo-tech §3.1) ──

/** Дней хранения журнала (с запасом на сдвиг часового пояса магазина). */
export const NOT_FOUND_JOURNAL_DAYS = 32;
/** Срок ключа дня в Redis, секунд: 33 дня (EXPIRE при каждой записи). */
export const NOT_FOUND_JOURNAL_TTL_SECONDS = 33 * 24 * 60 * 60;
/** Разных адресов в день на магазин; сверх — общий счётчик «прочие». */
export const NOT_FOUND_DAILY_LIMIT = 1000;
/** Скрытых адресов на магазин (решение §3а п. 7; 409 `site_not_found_hidden_limit`). */
export const NOT_FOUND_HIDDEN_LIMIT = 5000;
/** Предел длины пути до приведения, знаков (SB148). */
export const NOT_FOUND_RAW_PATH_MAX = 512;

/**
 * Предел длины приведённого пути — тот же, что у «откуда» переадресации
 * (SB131): путь длиннее нельзя переадресовать, писать его незачем.
 */
export const NOT_FOUND_PATH_MAX = SITE_REDIRECT_PATH_MAX;

// ── Ссылки из писем ──

/**
 * Префиксы адресов, по которым ходят ссылки с ключами из писем и сообщений:
 * ЛК (`/lk/setup?token=`, `/lk/reset`, `/lk/reviews?token=` — запрос отзыва,
 * `/lk/orders/<id>/approve`), подтверждение заказа (`/order-confirm/…`),
 * предпросмотр и пропуск сотрудника. Все они — служебные строки
 * `SITE_ROUTE_TABLE` (SB131), тест-страж это проверяет; отдельный список
 * нужен только ради понятной причины отказа (`email_link`).
 */
export const NOT_FOUND_EMAIL_LINK_PREFIXES: readonly string[] = Object.freeze([
  "/lk",
  "/order-confirm",
  "/site-preview",
  "/site-staff",
]);

// ── Класс посетителя по User-Agent ──

export const NOT_FOUND_AGENTS = ["visitor", "bot"] as const;
export type NotFoundAgent = (typeof NOT_FOUND_AGENTS)[number];

/**
 * Признаки поисковых роботов в User-Agent (нижний регистр, подстрока):
 * Яндекс, Google, Bing, Mail.ru (SB148). У роботов Яндекса (YandexBot,
 * YandexImages, YandexMetrika, YandexMobileBot…) общий вид
 * «compatible; Yandex…; +http://yandex.com/bots» — голое «yandex» не берём:
 * оно есть и у людей в приложении Яндекса («YandexSearch/…»), и они
 * считались бы поисковиками.
 */
export const BOT_USER_AGENT_MARKERS: readonly string[] = Object.freeze([
  "compatible; yandex",
  "yandex.com/bots",
  "googlebot",
  "google-inspectiontool",
  "bingbot",
  "mail.ru_bot",
]);

/** Поисковик или посетитель по заголовку User-Agent (нет заголовка — посетитель). */
export function classifyNotFoundAgent(userAgent: string | null | undefined): NotFoundAgent {
  if (typeof userAgent !== "string" || userAgent.length === 0) return "visitor";
  const ua = userAgent.toLowerCase();
  return BOT_USER_AGENT_MARKERS.some((marker) => ua.includes(marker)) ? "bot" : "visitor";
}

// ── Фильтр ──

/** Почему запрос не попал в журнал (для тестов и отладочного журнала витрины). */
export type NotFoundSkipReason =
  | "staff"
  | "preview"
  | "not_html"
  | "too_long"
  | "backslash"
  | "bad_encoding"
  | "dot_segment"
  | "extension"
  | "wp"
  | "cgi_bin"
  | "invalid"
  | "email_link"
  | "service"
  | "section";

export interface NotFoundJournalInput {
  /** Путь запроса как пришёл (может быть со строкой запроса — она отрезается). */
  path: string;
  /** Заголовок Accept; null — не пришёл. */
  accept: string | null;
  /** Заголовок User-Agent; null — не пришёл. */
  userAgent: string | null;
  /** Сотрудник с пропуском на закрытый сайт (SB138). */
  staff: boolean;
  /** Предпросмотр вида сайта или страницы (cookie предпросмотра). */
  preview: boolean;
}

export type NotFoundJournalDecision =
  | { record: false; reason: NotFoundSkipReason }
  | { record: true; path: string; agent: NotFoundAgent };

const skip = (reason: NotFoundSkipReason): NotFoundJournalDecision => ({ record: false, reason });

/** Расширение в последнем сегменте: «file.php», «sitemap.xml», «x.tar.gz». */
const EXTENSION = /.\.[^./]+$/u;

/** Сегменты раскодированного пути; null — битый UTF-8 / `%`-кодировка. */
function decodedSegments(rawPath: string): string[] | null {
  const out: string[] = [];
  for (const segment of rawPath.split("/")) {
    if (segment.length === 0) continue;
    try {
      out.push(decodeURIComponent(segment));
    } catch {
      return null;
    }
  }
  return out;
}

/**
 * Писать ли 404 в журнал и что именно (SB148). Никогда не бросает. Порядок
 * проверок — от дешёвых к дорогим; приведение и классификация последними.
 */
export function notFoundJournalDecision(input: NotFoundJournalInput): NotFoundJournalDecision {
  if (input.staff) return skip("staff");
  if (input.preview) return skip("preview");
  const agent = classifyNotFoundAgent(input.userAgent);
  // Картинки, скрипты, API-клиенты страниц не просят — их 404 владельцу не интересны.
  // Поисковики шлют `Accept: */*` — их по User-Agent пишем без проверки (решение §3а (в)).
  if (agent === "visitor" && (input.accept === null || !input.accept.toLowerCase().includes("text/html"))) {
    return skip("not_html");
  }

  const raw = typeof input.path === "string" ? input.path : "";
  // Строку запроса и якорь не храним (SEO2) и не учитываем в проверках пути.
  const cut = raw.search(/[?#]/u);
  const rawPath = cut >= 0 ? raw.slice(0, cut) : raw;
  if (rawPath.length > NOT_FOUND_RAW_PATH_MAX) return skip("too_long");
  if (rawPath.includes("\\") || /%5c/iu.test(rawPath)) return skip("backslash");

  const segments = decodedSegments(rawPath);
  if (segments === null) return skip("bad_encoding");
  if (segments.some((s) => s.startsWith("."))) return skip("dot_segment");
  const last = segments[segments.length - 1];
  if (last !== undefined && EXTENSION.test(last)) return skip("extension");
  if (segments.some((s) => s.toLowerCase().startsWith("wp-"))) return skip("wp");
  if (segments.some((s) => s.toLowerCase() === "cgi-bin")) return skip("cgi_bin");

  const normalized = normalizeSiteRedirectPath(rawPath);
  if (normalized === null || normalized.path.length > NOT_FOUND_PATH_MAX) return skip("invalid");

  const cls = classifySitePath(normalized.path);
  if (cls.kind === "service") {
    const isEmailLink = NOT_FOUND_EMAIL_LINK_PREFIXES.some(
      (prefix) => normalized.path === prefix || normalized.path.startsWith(`${prefix}/`),
    );
    return skip(isEmailLink ? "email_link" : "service");
  }
  if (cls.kind === "section") return skip("section");
  return { record: true, path: normalized.path, agent };
}

// ── Дни журнала по часовому поясу магазина ──

/** День журнала `yyyymmdd` (часть ключа Redis). */
export type NotFoundJournalDay = string;

/**
 * День журнала по часовому поясу магазина (`Shop.timeZone`), `yyyymmdd`.
 * Неизвестный пояс — UTC: журнал технический, лучше сдвинуть границу суток,
 * чем потерять запись (пояс проверяется при сохранении магазина).
 */
export function shopJournalDay(now: Date, timeZone: string): NotFoundJournalDay {
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" });
  } catch {
    formatter = new Intl.DateTimeFormat("en-CA", { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" });
  }
  const parts = formatter.formatToParts(now);
  const pick = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? "";
  return `${pick("year")}${pick("month")}${pick("day")}`;
}

/** `20261025` → `2026-10-25` (поле `lastDay` и период в ответе кабинета). */
export function journalDayToIso(day: NotFoundJournalDay): string {
  return `${day.slice(0, 4)}-${day.slice(4, 6)}-${day.slice(6, 8)}`;
}

/** Календарный сдвиг дня журнала на `delta` суток (без часовых поясов — это уже дата). */
export function shiftJournalDay(day: NotFoundJournalDay, delta: number): NotFoundJournalDay {
  const date = new Date(Date.UTC(Number(day.slice(0, 4)), Number(day.slice(4, 6)) - 1, Number(day.slice(6, 8)) + delta));
  const yyyy = String(date.getUTCFullYear()).padStart(4, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${yyyy}${mm}${dd}`;
}

// ── Периоды кабинета (решение §3а п. 8) ──

/** «Сегодня · 7 дней · 30 дней» (= день / неделя / месяц SB148), сегодня включительно. */
export const NOT_FOUND_PERIODS = ["day", "week", "month"] as const;
export type NotFoundPeriod = (typeof NOT_FOUND_PERIODS)[number];

export const NOT_FOUND_PERIOD_DAYS: Readonly<Record<NotFoundPeriod, number>> = Object.freeze({
  day: 1,
  week: 7,
  month: 30,
});

export const NOT_FOUND_PERIOD_LABELS: Readonly<Record<NotFoundPeriod, string>> = Object.freeze({
  day: "Сегодня",
  week: "7 дней",
  month: "30 дней",
});

/** Период по умолчанию во вкладке «Не найдено». */
export const NOT_FOUND_DEFAULT_PERIOD: NotFoundPeriod = "week";

export function isNotFoundPeriod(value: unknown): value is NotFoundPeriod {
  return typeof value === "string" && (NOT_FOUND_PERIODS as readonly string[]).includes(value);
}

/** Дни периода от сегодняшнего (по поясу магазина) назад, сегодня — первым. */
export function notFoundPeriodDays(period: NotFoundPeriod, now: Date, timeZone: string): NotFoundJournalDay[] {
  const today = shopJournalDay(now, timeZone);
  return Array.from({ length: NOT_FOUND_PERIOD_DAYS[period] }, (_, i) => shiftJournalDay(today, -i));
}

// ── DTO кабинета (db-design-seo-tech §4.2, §4.3) ──

/**
 * Статус строки (считается при чтении):
 *   - `open` — правила нет, по адресу ничего живого;
 *   - `redirected` — «переадресован» (правило действует, как в proxy);
 *   - `rule_inactive` — «правило не действует» (цель скрыта/удалена, петля);
 *   - `live_again` — «адрес снова открывается».
 */
export const NOT_FOUND_ROW_STATUSES = ["open", "redirected", "rule_inactive", "live_again"] as const;
export type NotFoundRowStatus = (typeof NOT_FOUND_ROW_STATUSES)[number];

export const NOT_FOUND_ROW_STATUS_LABELS: Readonly<Record<NotFoundRowStatus, string>> = Object.freeze({
  open: "—",
  redirected: "переадресован",
  rule_inactive: "правило не действует",
  live_again: "адрес снова открывается",
});

/**
 * Обработанные строки — по умолчанию (`all=0`) скрыты под фильтром
 * «Обработанные и скрытые» (решение §3а п. 11). `rule_inactive` и `open`
 * видны: они требуют внимания.
 */
export function isNotFoundRowHandled(row: { status: NotFoundRowStatus; hidden: boolean }): boolean {
  return row.hidden || row.status === "redirected" || row.status === "live_again";
}

export const NOT_FOUND_SORTS = ["total", "visitors", "bots"] as const;
export type NotFoundSort = (typeof NOT_FOUND_SORTS)[number];

/** Правило переадресации, найденное для строки (`SiteRedirect`). */
export interface NotFoundRowRedirectDto {
  id: string;
  /** Prisma-enum `SiteRedirectSource`. */
  source: "AUTO" | "MANUAL" | "IMPORT";
  /** Куда ведёт (адрес для людей). */
  targetHref: string;
}

export interface NotFoundJournalRowDto {
  /** Приведённый путь (как `SiteRedirect.fromPath`), ключ строки. */
  path: string;
  /** Раскодированный путь для показа (кириллица буквами). */
  display: string;
  visitors: number;
  bots: number;
  /** Последний день с обращениями, `YYYY-MM-DD` по поясу магазина. */
  lastDay: string;
  status: NotFoundRowStatus;
  hidden: boolean;
  redirect?: NotFoundRowRedirectDto;
}

/** Ответ `GET /admin/site/not-found`. */
export interface NotFoundJournalDto {
  rows: NotFoundJournalRowDto[];
  /** Строк после фильтра (до постраничной выдачи). */
  total: number;
  /** Обращения сверх предела адресов в день (поле «прочие»). */
  other: { visitors: number; bots: number };
  /** Скрытых адресов магазина. */
  hiddenCount: number;
  /** Обработанных и скрытых строк периода (счётчик у фильтра). */
  handledCount: number;
  period: { key: NotFoundPeriod; from: string; to: string };
  /** «Журнал ведётся с …» (`YYYY-MM-DD`); null — записей нет. */
  since: string | null;
}

/** Раскодированный путь для показа; битая кодировка — как есть. */
export function notFoundDisplayPath(path: string): string {
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
}
